import { ed25519 } from "@noble/curves/ed25519.js";

/**
 * ed25519 sign / verify / public-key helpers. Thin wrappers around
 * `@noble/curves/ed25519` so a future hardening swap (e.g.
 * WebCrypto `subtle.importKey` with `extractable: false` — ADR-008
 * v0.2 §6.3 carry) only touches this file.
 *
 * D5 (2026-05-14) carry: ed25519-only for the current scope. The
 * hybrid (ed25519 + ml-dsa-65) extension hook lives in this module
 * too — any future second-curve attachment ships as a sibling
 * export (`signHybrid` / `verifyHybrid`) without disturbing the
 * legacy single-sig API.
 *
 * @noble/curves uses RFC 8032 deterministic signing
 * (k = H(privkey, msg) — no random nonce), so a given (privkey,
 * msg) pair always produces the same 64-byte signature. This is
 * the property the cross-impl envelope test exploits to
 * prove byte-equal output against the Rust postcard / ed25519
 * pipeline.
 */

/**
 * Sign a 32-byte payload with the 32-byte ed25519 signing key. The
 * caller is expected to have computed the payload via
 * `signPayloadAction` / `signPayloadSeedContrib` from `envelope.ts`
 * — this helper does NOT re-prefix any domain separator.
 */
export function sign(payload: Uint8Array, signingKey: Uint8Array): Uint8Array {
  if (signingKey.length !== 32) {
    throw new RangeError(
      `ed25519.sign: signing key must be 32 bytes, got ${signingKey.length}`,
    );
  }
  return ed25519.sign(payload, signingKey);
}

/**
 * Verify a 64-byte signature against the given message + 32-byte
 * verifying key (player_id). Returns `true` iff the signature is
 * valid. Used by the future settle audit path that
 * re-verifies broadcast signatures against the public seat
 * roster.
 */
export function verify(
  signature: Uint8Array,
  message: Uint8Array,
  verifyingKey: Uint8Array,
): boolean {
  if (signature.length !== 64) return false;
  if (verifyingKey.length !== 32) return false;
  try {
    return ed25519.verify(signature, message, verifyingKey);
  } catch {
    return false;
  }
}

/**
 * Derive the 32-byte ed25519 verifying key (a.k.a. `player_id`)
 * from a 32-byte signing key seed. Same mapping as Rust
 * `arkhe_holdem_primitives::player_id::PlayerKey::from_seed +
 * player_id()`.
 */
export function getPublicKey(signingKey: Uint8Array): Uint8Array {
  if (signingKey.length !== 32) {
    throw new RangeError(
      `ed25519.getPublicKey: signing key must be 32 bytes, got ${signingKey.length}`,
    );
  }
  return ed25519.getPublicKey(signingKey);
}
